import { CATALOGUE_MODE } from "@constants";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";
import Modal from "react-modal";

import { store } from "@store";

import {
    getReadableTextColor,
    updateCustomerOptin,
} from "@helpers/marketingOptin";

import { successToast } from "@components/common/Toast";

import {
    setIsMarketingOptinModalOpened,
    setUserHasOptin,
    kioskStore,
} from "@pages/Kiosk/store";

import "./index.css";

export function MarketingOptinModal(): JSX.Element {
    const {
        isPrm,
        isMarketingOptinModalOpened,
        user,
        project: {
            template: {
                content: { generalDesign },
            },
        },
    } = useSnapshot(kioskStore);
    const { middlewareApiUrl } = useSnapshot(store);
    // Ref (not state) because the modal closes synchronously on click — a
    // state-driven "submitting" flag would never render. The component stays
    // mounted across the session (react-modal only toggles its portal), so the
    // ref is reset in `finally` to guard a same-tick double-fire WITHOUT leaving
    // it stuck `true` and locking the modal for the next customer.
    const submittingRef = React.useRef(false);

    const primaryColor =
        (generalDesign as GeneralDesignType).colors?.[0]?.content ?? "#000000";
    const primaryTextColor = getReadableTextColor(primaryColor);

    // Only an explicit decision persists `hasOptin` via the API:
    //   Activer        -> true   (opt-in)
    //   opt-out button -> false  (explicit opt-out)
    //   Pas maintenant -> null   (deferred / no decision) — NO API call
    // The modal always closes immediately on click (kiosk: never trap the user),
    // so there is intentionally NO error feedback: a failed write leaves
    // `hasOptin` unrecorded, which is safe — the modal simply re-prompts on the
    // next login (shouldPromptMarketingOptin returns true while undecided).
    async function submitOptin(value: boolean | null) {
        if (submittingRef.current) {
            return;
        }
        setIsMarketingOptinModalOpened(false);
        // A deferred choice (null) records nothing: just close, no API call.
        if (value === null) {
            return;
        }
        if (!user.phoneNumber) {
            console.warn(
                "Marketing opt-in skipped: authenticated user has no phone number"
            );
            return;
        }
        submittingRef.current = true;
        try {
            const success = await updateCustomerOptin(
                middlewareApiUrl,
                user.phoneNumber,
                value
            );
            if (success) {
                // Keep the local store in sync with the explicit decision so a
                // re-authentication in the same session does not re-open the modal.
                setUserHasOptin(value);
                // Confirm only an explicit opt-in (Activer). The opt-out button
                // also reaches this branch (value === false) but gets no toast.
                if (value === true) {
                    successToast(
                        t("You are subscribed to email and SMS communications")
                    );
                }
            } else {
                // No UI feedback by design (modal already closed): a failed write
                // leaves hasOptin unrecorded — safe, the modal re-prompts on the
                // next login. Logged so failures are still diagnosable.
                console.warn(
                    "updateCustomer opt-in not persisted (hasOptin stays unrecorded, will re-prompt next login)"
                );
            }
        } finally {
            // Always release the guard, even on success/failure/throw, so the
            // modal is never permanently locked for the next customer.
            submittingRef.current = false;
        }
    }

    return (
        <Modal
            isOpen={isMarketingOptinModalOpened}
            onRequestClose={() => submitOptin(null)}
            contentLabel="marketing-optin-modal"
            role="dialog"
            shouldFocusAfterRender={true}
            shouldReturnFocusAfterClose={true}
            aria={{
                modal: true,
                labelledby: "marketing-optin-title",
                describedby: "marketing-optin-desc",
            }}
            style={{
                overlay: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: isPrm ? "end" : "center",
                    backgroundColor: "#747474c4",
                    zIndex: 2,
                },
                content: {
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    padding: "60px",
                    color: "black",
                    height: "unset",
                    width: "900px",
                    fontSize: "var(--font-size-title)",
                    zIndex: 2,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "40px",
                    border: "1px solid #707070",
                    borderTop: `15px solid ${primaryColor}`,
                    bottom: isPrm ? "10px" : "unset",
                    top: "unset",
                    right: "unset",
                    left: "unset",
                    gap: "40px",
                    zoom: `${
                        process.env.REACT_APP_MODE === CATALOGUE_MODE
                            ? "50%"
                            : "100%"
                    }`,
                },
            }}
        >
            <h2 id="marketing-optin-title" className="marketing-optin-title">
                {t("Receive your rewards and promo codes by email and SMS")}
            </h2>
            <span
                id="marketing-optin-desc"
                className="marketing-optin-description"
            >
                {t(
                    "Enable your email and SMS communications to be the first to hear about our new products, exclusive offers and gifts reserved for our loyal customers"
                )}
            </span>
            <div className="marketing-optin-actions">
                <button
                    type="button"
                    className="marketing-optin-button marketing-optin-button--secondary"
                    onClick={() => submitOptin(null)}
                >
                    {t("Not now")}
                </button>
                <button
                    type="button"
                    className="marketing-optin-button marketing-optin-button--primary"
                    style={{
                        backgroundColor: primaryColor,
                        color: primaryTextColor,
                    }}
                    onClick={() => submitOptin(true)}
                >
                    {t("Activate")}
                </button>
            </div>
            {/* Tertiary opt-out: intentionally a low-weight link rendered last
                (after the two primary actions in DOM/tab order) — it is the
                least-likely choice, kept discoverable but visually de-emphasised. */}
            <button
                type="button"
                className="marketing-optin-optout"
                onClick={() => submitOptin(false)}
            >
                {t("I no longer want to receive this message")}
            </button>
        </Modal>
    );
}
